import axios, { AxiosResponse, AxiosError } from 'axios';
import { 
  ApiResponse, 
  PaginatedResponse,
  PurchaseOrder, 
  ExpectedItem,
  InventoryItem,
  UnmatchedItem,
  Vendor,
  DashboardData,
  ScanRequest,
  MatchResult,
  ImportFormData,
  ScanFormData
} from '../types';

// Create axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8787',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: async (loginData: { email: string; password: string; loginType?: string }): Promise<ApiResponse<{ token: string; user: any }>> => {
    const response = await api.post('/api/auth/login', loginData);
    return response.data;
  },

  verify: async (): Promise<ApiResponse<{ valid: boolean; user: any }>> => {
    const response = await api.get('/auth/verify');
    return response.data;
  },

  refresh: async (userId: string): Promise<ApiResponse<{ token: string; user: any }>> => {
    const response = await api.post('/auth/refresh', { userId });
    return response.data;
  },

  logout: async (userId: string): Promise<ApiResponse> => {
    const response = await api.post('/auth/logout', { userId });
    return response.data;
  },
};

// Purchase Orders API
export const purchaseOrderApi = {
  import: async (formData: FormData): Promise<ApiResponse<{ po_id: string; total_items: number; message: string }>> => {
    const response = await api.post('/api/purchase-orders/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  list: async (params: {
    vendor_code?: string;
    status?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<ApiResponse<PaginatedResponse<PurchaseOrder>>> => {
    const response = await api.get('/api/purchase-orders', { params });
    return response.data;
  },

  get: async (id: string): Promise<ApiResponse<{
    purchase_order: PurchaseOrder;
    items: ExpectedItem[];
  }>> => {
    const response = await api.get(`/api/purchase-orders/${id}`);
    return response.data;
  },

  updateStatus: async (id: string, status: string): Promise<ApiResponse> => {
    const response = await api.put(`/api/purchase-orders/${id}/status`, { status });
    return response.data;
  },
};

// Inventory API
export const inventoryApi = {
  scan: async (scanData: ScanRequest): Promise<ApiResponse<MatchResult>> => {
    const response = await api.post('/api/inventory/scan', scanData);
    return response.data;
  },

  manualMatch: async (tempId: string, itemId: string): Promise<ApiResponse<{ batch_id: string; message: string }>> => {
    const response = await api.post('/api/inventory/match', { temp_id: tempId, item_id: itemId });
    return response.data;
  },

  getBatch: async (batchId: string): Promise<ApiResponse<InventoryItem>> => {
    const response = await api.get(`/api/inventory/batch/${batchId}`);
    return response.data;
  },

  getRecent: async (params: {
    limit?: number;
    vendor_code?: string;
  } = {}): Promise<ApiResponse<InventoryItem[]>> => {
    const response = await api.get('/api/inventory', { params });
    return response.data;
  },
};

// Unmatched Items API
export const unmatchedApi = {
  list: async (params: {
    vendor_code?: string;
    status?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<ApiResponse<PaginatedResponse<UnmatchedItem> & { stats: any }>> => {
    const response = await api.get('/api/unmatched', { params });
    return response.data;
  },

  resolve: async (id: string, resolution: string, notes?: string): Promise<ApiResponse> => {
    const response = await api.post(`/api/unmatched/${id}/resolve`, { resolution, notes });
    return response.data;
  },

  autoMatch: async (poId: string): Promise<ApiResponse<{ matched: number; message: string }>> => {
    const response = await api.post('/api/unmatched/auto-match', { po_id: poId });
    return response.data;
  },

  delete: async (id: string): Promise<ApiResponse> => {
    const response = await api.delete(`/api/unmatched/${id}`);
    return response.data;
  },
};

// Batch Counter API
export const batchCounterApi = {
  getCounters: async (): Promise<ApiResponse<Record<string, number>>> => {
    const response = await api.get('/api/batch-counters');
    return response.data;
  },

  setCounter: async (vendorCode: string, counterValue: number): Promise<ApiResponse<{ message: string }>> => {
    const response = await api.post('/api/batch-counters', {
      vendor_code: vendorCode,
      counter_value: counterValue
    });
    return response.data;
  },
};

// Vendors API
export const vendorApi = {
  list: async (status?: string): Promise<ApiResponse<Vendor[]>> => {
    const response = await api.get('/api/vendors', { params: { status } });
    return response.data;
  },

  get: async (code: string): Promise<ApiResponse<{
    vendor: Vendor;
    performance: any;
  }>> => {
    const response = await api.get(`/api/vendors/${code}`);
    return response.data;
  },

  create: async (vendorData: Partial<Vendor>): Promise<ApiResponse<Vendor>> => {
    const response = await api.post('/api/vendors', vendorData);
    return response.data;
  },

  update: async (code: string, updates: Partial<Vendor>): Promise<ApiResponse> => {
    const response = await api.put(`/api/vendors/${code}`, updates);
    return response.data;
  },
};

// Reports API
export const reportsApi = {
  dashboard: async (): Promise<ApiResponse<DashboardData>> => {
    const response = await api.get('/api/dashboard/stats');
    return response.data;
  },

  inventory: async (params: {
    start_date?: string;
    end_date?: string;
    vendor_code?: string;
  } = {}): Promise<ApiResponse<{
    items: InventoryItem[];
    stats: any;
    total: number;
  }>> => {
    const response = await api.get('/api/inventory', { params });
    return response.data;
  },

  vendorPerformance: async (params: {
    start_date?: string;
    end_date?: string;
  } = {}): Promise<ApiResponse<any[]>> => {
    const response = await api.get('/api/vendors', { params });
    return response.data;
  },

  export: async (type: string, format: string, filters: any): Promise<ApiResponse<{
    export_id: string;
    url: string;
    expires_at: string;
  }>> => {
    const response = await api.post('/api/reports/export', { type, format, filters });
    return response.data;
  },
};

// Health check
export const healthCheck = async (): Promise<any> => {
  const response = await api.get('/health');
  return response.data;
};

export default api;